import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { type ArticleGroup } from '@sr/types';
import { WithCustomerPortalApi } from 'core/customerPortalApi';
import { useProfileV2 } from 'core/lib/Profile';
import { getApiUrl } from '#lib/utils/environment';

export type GetArticleGroupsProperties = {
  selectedBusinessPartners: string[];
  useGrandParentLocation?: boolean;
};

export type GetArticleGroupResultType = {
  articles: ArticleGroup[];
} & UseQueryResult<ArticleGroup[]>;

export const useArticleGroups = WithCustomerPortalApi<
  ArticleGroup[],
  GetArticleGroupResultType,
  GetArticleGroupsProperties
>(
  (fetchFn, { selectedBusinessPartners, useGrandParentLocation }) => {
    const { profile } = useProfileV2();

    const query = useQuery({
      queryKey: [
        'article-groups',
        selectedBusinessPartners,
        profile?.language,
        useGrandParentLocation ?? false,
      ],

      queryFn: ({ signal }) =>
        fetchFn({
          url: getApiUrl() + '/articles/groups',
          options: {
            method: 'POST',
            signal,
            body: JSON.stringify({
              dwKeys: selectedBusinessPartners,
              IsMaterialArticle: true,
              includeSiblings: true,
              useGrandParentLocation,
            }),
          },
        }),
    });

    return {
      articles: query.data ?? [],
      ...query,
    };
  },
  { hookName: 'useArticleGroups' },
);
